import * as ed from "@noble/ed25519";
import { sha512 } from "@noble/hashes/sha512";

import type { SignatureRecord } from "../types";
import { canonicalBytes } from "./canonical";
import { sha256, sha256Hex } from "./hash";

// @noble/ed25519 v2 prefers crypto.subtle.digest('SHA-512', ...). It's available
// in browsers but not in jsdom (test env) and only Node ≥19. Wiring sha512 from
// @noble/hashes makes verify work everywhere; production browsers still use the
// faster subtle path when available.
ed.etc.sha512Async = (...m) => Promise.resolve(sha512(ed.etc.concatBytes(...m)));

const decodeBase64 = (b64: string): Uint8Array =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export async function verifyEnvelopeSignature(
  envelope: Record<string, unknown>,
  record: SignatureRecord,
  publicKeyBase64: string,
): Promise<boolean> {
  const payload = canonicalBytes(envelope);
  const computedDigest = sha256Hex(payload);
  if (computedDigest !== record.envelope_sha256) {
    return false;
  }
  if (record.alg !== "ed25519") {
    return false;
  }
  const pubKey = decodeBase64(publicKeyBase64);
  const sig = decodeBase64(record.signature_b64);
  try {
    return await ed.verifyAsync(sig, payload, pubKey);
  } catch {
    return false;
  }
}

export { sha256 };
